import { apiFetch, apiFetchBlob } from './client.js';

/**
 * Money leaving a bank we operate by hand.
 *
 * There is no payment API behind any of this. A customer asks, an officer
 * decides, and a member of staff sends the transfer from the bank's own
 * interface and comes back to record what happened. The lifecycle is the whole
 * feature: at each step the console has to say plainly whether the money has
 * moved yet, because nothing else in the system knows.
 */

export type WithdrawalStatus = 'REQUESTED' | 'APPROVED' | 'SENT' | 'REJECTED' | 'FAILED';

export interface WithdrawalRequest {
  agreedFee?: { feeCents: string; totalDebitCents: string; feeAccountId: string; feeAccountIban: string; quoteId: string } | null;
  id: string;
  customerId: string;
  accountId: string;
  provider: string;
  /** What the customer asked for, in minor units. */
  amountCents: string;
  currency: string;
  beneficiaryName: string;
  beneficiaryAccount: string;
  beneficiaryBank: string | null;
  beneficiaryBic: string | null;
  /** What the customer wants written on the transfer. */
  reference: string | null;
  status: string;
  decisionReason: string | null;
  /** The bank's own reference, once staff have actually sent it. */
  bankReference: string | null;
  /** An officer's outstanding question, and the customer's answer. */
  infoRequestMessage?: string | null;
  infoRequestedAt?: string | null;
  infoAnswer?: string | null;
  infoAnsweredAt?: string | null;
  requestedAt: string;
  decidedBy: string | null;
  /** The decision is in the comment thread (DEV-95). */
  decisionOnFile?: boolean;
  decidedAt: string | null;
  sentAt: string | null;
}

/** Evidence the customer attached: what this transfer is for. */
export interface WithdrawalDocument {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

export const withdrawalsApi = {
  bankEvidence: (id: string): Promise<{ instruction: unknown; bankStatus: string | null; error: string | null; observations: unknown[] }> => apiFetch(`/admin/withdrawals/${id}/bank-evidence`),
  execute: (id: string): Promise<WithdrawalRequest> => apiFetch(`/admin/withdrawals/${id}/execute`, { method: 'POST', body: {} }),
  refreshBank: (id: string): Promise<WithdrawalRequest> => apiFetch(`/admin/withdrawals/${id}/refresh-bank`, { method: 'POST', body: {} }),
  get: (id: string): Promise<WithdrawalRequest> => apiFetch(`/admin/withdrawals/${id}`),
  list: (status = 'REQUESTED'): Promise<WithdrawalRequest[]> =>
    apiFetch<WithdrawalRequest[]>(`/admin/withdrawals?status=${status}`),

  /**
   * What the customer attached. Metadata only — the bytes come one deliberate
   * click at a time, and every read is logged against the operator who asked.
   */
  documents: (id: string): Promise<WithdrawalDocument[]> =>
    apiFetch<WithdrawalDocument[]>(`/admin/withdrawals/${id}/documents`),

  /** The bytes, fetched with the session's token rather than a bare link. */
  document: (id: string, docId: string): Promise<Blob> =>
    apiFetchBlob(`/admin/withdrawals/${id}/documents/${docId}`),

  /** Accept it — and debit the customer, because the decision commits the money. */
  approve: (id: string, reason: string): Promise<WithdrawalRequest> =>
    apiFetch<WithdrawalRequest>(`/admin/withdrawals/${id}/approve`, {
      method: 'POST',
      body: reason.trim() === '' ? {} : { reason },
    }),

  /** Record that it was actually sent, with the reference the bank gave it. */
  markSent: (id: string, bankReference: string): Promise<WithdrawalRequest> =>
    apiFetch<WithdrawalRequest>(`/admin/withdrawals/${id}/sent`, {
      method: 'POST',
      body: { bankReference },
    }),

  /** Ask the customer something instead of refusing to get it. */
  requestInfo: (id: string, message: string): Promise<WithdrawalRequest> =>
    apiFetch<WithdrawalRequest>(`/admin/withdrawals/${id}/request-info`, {
      method: 'POST',
      body: { message },
    }),

  /** Refuse it before anything moves. */
  reject: (id: string, reason: string): Promise<WithdrawalRequest> =>
    apiFetch<WithdrawalRequest>(`/admin/withdrawals/${id}/reject`, {
      method: 'POST',
      body: { reason },
    }),

  /** The bank refused it after we debited — give the money back. */
  markFailed: (id: string, reason: string): Promise<WithdrawalRequest> =>
    apiFetch<WithdrawalRequest>(`/admin/withdrawals/${id}/failed`, {
      method: 'POST',
      body: { reason },
    }),

  /** Write down a transfer the bank has already made. Debits immediately. */
  recordManual: (input: {
    provider: string;
    customerId: string;
    accountId: string;
    /** Exact minor units as a string; a float here is not the bank's amount. */
    amountCents: string;
    currency: string;
    beneficiaryName: string;
    beneficiaryAccount: string;
    beneficiaryBank?: string;
    beneficiaryBic?: string;
    reference?: string;
    bankReference: string;
    valueDate: string;
  }): Promise<{ id: string }> =>
    apiFetch('/admin/withdrawals/manual', { method: 'POST', body: input }),

  commission: (id: string): Promise<OutgoingCommission> =>
    apiFetch(`/admin/withdrawals/${id}/commission`),

  /** Change the commission before approval charges it. */
  editFee: (id: string, input: CommissionChange, operationId: string): Promise<OutgoingCommission> =>
    apiFetch(`/admin/withdrawals/${id}/edit-fee`, {
      method: 'POST',
      headers: { 'Idempotency-Key': operationId },
      body: input,
    }),

  /** Append a difference to a commission already charged. */
  correctFee: (id: string, input: CommissionChange, operationId: string): Promise<OutgoingCommission> =>
    apiFetch(`/admin/withdrawals/${id}/correct-fee`, {
      method: 'POST',
      headers: { 'Idempotency-Key': operationId },
      body: input,
    }),

  /**
   * Correct what the customer asked us to send, before anyone approves it.
   * Separate from editFee: this is their instruction, not our charge.
   */
  editAmount: (
    id: string,
    input: { amountCents: string; expectedAmountCents: string; expectedRevision: number; reason: string },
    operationId: string,
  ): Promise<{ id: string; amountCents: string; originalAmountCents: string | null; amountEditRevision: number; currency: string; status: string }> =>
    apiFetch(`/admin/withdrawals/${id}/amount`, {
      method: 'POST',
      headers: { 'Idempotency-Key': operationId },
      body: input,
    }),

  /** What the customer declared at onboarding, against this payment. */
  expectedActivity: (id: string): Promise<ExpectedActivity> =>
    apiFetch(`/admin/withdrawals/${id}/expected-activity`),

  /** What this customer has sent to this beneficiary before. */
  counterparty: (id: string): Promise<CounterpartyHistory> =>
    apiFetch(`/admin/withdrawals/${id}/counterparty`),

  resumeCommission: (id: string, operationId: string): Promise<OutgoingCommission> =>
    apiFetch(`/admin/withdrawals/${id}/commission/${operationId}/resume`, { method: 'POST' }),
};

export interface CommissionChange {
  feeCents: string;
  expectedFeeCents: string;
  expectedRevision: number;
  reason: string;
}

export interface OutgoingCommissionChange {
  operationId: string;
  beforeCents: string;
  afterCents: string;
  officerId: string;
  reason: string;
  createdAt: string;
}

/** What the customer told us at onboarding they would do. */
export interface ExpectedActivity {
  declaredMaxSingle: string | null;
  declaredOutgoingVolume: string | null;
  declaredOutgoingCount: string | null;
  declaredMaxSingleCents: string | null;
  maxSingleUnreadable: boolean;
  paymentCents: string;
  currency: string;
  overDeclaredMax: boolean;
  noDeclaration: boolean;
}

/** Facts about the party being paid, from payments we have already seen. */
export interface CounterpartyHistory {
  priorPayments: number;
  settledPayments: number;
  settledCents: string;
  currency: string;
  firstPaidAt: string | null;
  lastPaidAt: string | null;
  refusedPayments: number;
  firstTime: boolean;
  otherCustomers: number;
  largestPriorCents: string | null;
  unusuallyLarge: boolean;
}

export interface OutgoingCommission {
  status: string;
  amountCents: string;
  currency: string;
  /** What the customer approved under SCA. Null when nothing was quoted. */
  agreedFeeCents: string | null;
  feeCents: string;
  editable: boolean;
  editRevision: number;
  overrideFeeCents: string | null;
  /** What the customer submitted, if staff have since corrected it. */
  originalAmountCents: string | null;
  amountEditRevision: number;
  /**
   * The amount the commission was priced against. The fee is a percentage, so
   * when this no longer matches `amountCents` the commission is stale and
   * somebody has to decide what it should be.
   */
  feeQuotedForCents: string | null;
  charged: {
    feeAccountId: string;
    originalFeeCents: string;
    feeCents: string;
    revision: number;
    refunded: boolean;
    changes: OutgoingCommissionChange[];
  } | null;
  verificationError: string | null;
  pendingOperationId: string | null;
  history: {
    id: string;
    kind: string;
    status: string;
    officerId: string;
    reason: string;
    createdAt: string;
    completedAt: string | null;
    request: { feeCents: string; expectedFeeCents: string; expectedRevision: number } | null;
  }[];
}
